import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Pin, EyeOff, Eye, Trash2, MessageCircle, Send } from "lucide-react";
import { StarRating } from "@/components/StarRating";

const IMG = (u) => (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL || ""}${u}`);

export default function AdminReviews() {
  const nav = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [replyingId, setReplyingId] = useState(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [savingReply, setSavingReply] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const runNudgeSweep = async () => {
    setSweeping(true);
    try {
      const { data } = await api.post("/admin/review-nudge/sweep-now");
      if (data.sent > 0) {
        toast.success(`Nudge sent to ${data.sent} of ${data.candidates} eligible customer${data.candidates === 1 ? "" : "s"}.`);
      } else if (data.candidates > 0) {
        toast.success(`Checked ${data.candidates} delivered order${data.candidates === 1 ? "" : "s"} — none needed a nudge (already reviewed or already nudged).`);
      } else {
        toast.success("No delivered orders are due for a nudge yet (14+ days after delivery).");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sweep failed");
    } finally {
      setSweeping(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? "" : `?status_filter=${filter}`;
      const { data } = await api.get(`/admin/reviews${params}`);
      setReviews(data);
    } catch {
      toast.error("Couldn't load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const update = async (id, patch) => {
    try {
      const { data } = await api.put(`/admin/reviews/${id}`, patch);
      setReviews((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const startReply = (r) => {
    setReplyingId(r.id);
    setReplyDraft(r.owner_reply || "");
  };

  const cancelReply = () => {
    setReplyingId(null);
    setReplyDraft("");
  };

  const saveReply = async (id, clear = false) => {
    setSavingReply(true);
    try {
      const { data } = await api.put(`/admin/reviews/${id}`, { owner_reply: clear ? "" : replyDraft.trim() });
      setReviews((prev) => prev.map((r) => (r.id === id ? data : r)));
      setReplyingId(null);
      setReplyDraft("");
      toast.success(clear ? "Reply removed" : "Reply posted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingReply(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("jenova_admin_token");
    nav("/admin/login");
  };

  const counts = {
    all: reviews.length,
    published: reviews.filter((r) => r.status === "published").length,
    hidden: reviews.filter((r) => r.status === "hidden").length,
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <Link to="/admin" className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-[#1A1C1A] inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to admin
          </Link>
          <Button variant="ghost" onClick={logout} className="text-xs uppercase tracking-[0.2em]">
            <LogOut className="w-4 h-4 mr-1" strokeWidth={1.5} /> Log out
          </Button>
        </div>

        <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-semibold mb-2">Moderation</p>
        <h1 className="font-serif text-4xl font-light tracking-tight text-[#1A1C1A]">Reviews</h1>
        <p className="text-sm text-stone-500 mt-2">Approve, hide, pin to the home carousel, or delete customer reviews.</p>

        {/* Review-nudge automation card */}
        <div className="mt-6 border border-[#E5E2DC] rounded-xl p-5 bg-white flex flex-col md:flex-row md:items-center gap-4" data-testid="review-nudge-card">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500">Automation</p>
            <p className="text-sm text-[#1A1C1A] mt-1"><strong>Review nudges</strong> auto-send 14 days after delivery to customers who haven't reviewed yet.</p>
            <p className="text-xs text-stone-500 mt-1">Runs hourly in the background. Skips anyone who already left a review or was already nudged.</p>
          </div>
          <Button
            data-testid="review-nudge-sweep-now"
            onClick={runNudgeSweep}
            disabled={sweeping}
            variant="outline"
            className="rounded-full"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            {sweeping ? "Checking…" : "Run sweep now"}
          </Button>
        </div>

        <div className="mt-6 flex gap-2">
          {[["all", "All"], ["published", "Live"], ["hidden", "Hidden"]].map(([k, label]) => (
            <Button
              key={k}
              data-testid={`admin-reviews-filter-${k}`}
              variant={filter === k ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(k)}
              className={`rounded-full ${filter === k ? "bg-[#1A1C1A] hover:bg-[#3C3E3C]" : ""}`}
            >
              {label} ({counts[k] ?? "…"})
            </Button>
          ))}
        </div>

        {loading && <p className="mt-8 text-stone-500 italic">Loading…</p>}
        {!loading && reviews.length === 0 && (
          <div className="mt-8 py-16 text-center border border-dashed border-[#E5E2DC] rounded-xl">
            <p className="font-serif text-xl italic text-stone-500">No reviews {filter !== "all" ? `in ${filter}` : "yet"}.</p>
          </div>
        )}

        {!loading && reviews.length > 0 && (
          <div className="mt-6 space-y-4">
            {reviews.map((r) => (
              <div key={r.id} className={`bg-white border rounded-xl p-6 ${r.status === "hidden" ? "border-red-100 opacity-70" : "border-[#E5E2DC]"}`} data-testid="admin-review-row">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="text-sm font-semibold text-[#1A1C1A]">{r.author_name}</p>
                      {r.verified_purchase && <span className="text-[10px] uppercase tracking-[0.15em] text-[#3F5F3F]">Verified</span>}
                      {r.pinned && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#B58B47]"><Pin className="w-3 h-3" strokeWidth={2} /> Pinned</span>}
                      {r.status === "hidden" && <span className="text-[10px] uppercase tracking-[0.15em] text-red-600">Hidden</span>}
                    </div>
                    <StarRating value={r.rating} />
                    {r.product_name && <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-stone-400">on {r.product_name}</p>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button data-testid="admin-review-pin" size="sm" variant="outline" className="rounded-full" onClick={() => update(r.id, { pinned: !r.pinned })}>
                      <Pin className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> {r.pinned ? "Unpin" : "Pin to home"}
                    </Button>
                    {r.status === "published" ? (
                      <Button data-testid="admin-review-hide" size="sm" variant="outline" className="rounded-full" onClick={() => update(r.id, { status: "hidden" })}>
                        <EyeOff className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Hide
                      </Button>
                    ) : (
                      <Button data-testid="admin-review-publish" size="sm" variant="outline" className="rounded-full" onClick={() => update(r.id, { status: "published" })}>
                        <Eye className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Publish
                      </Button>
                    )}
                    <Button data-testid="admin-review-delete" size="sm" variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-50" onClick={() => remove(r.id)}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Delete
                    </Button>
                  </div>
                </div>
                {r.title && <h3 className="font-serif text-lg font-light mb-1">{r.title}</h3>}
                <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{r.body}</p>
                {r.image_urls?.length > 0 && (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {r.image_urls.map((u, i) => (
                      <a key={i} href={IMG(u)} target="_blank" rel="noreferrer">
                        <img src={IMG(u)} alt="" className="w-16 h-16 object-cover rounded-md" />
                      </a>
                    ))}
                  </div>
                )}

                {/* Owner reply (published + editor) */}
                {r.owner_reply && replyingId !== r.id && (
                  <div className="mt-4 border-l-2 border-[#1A1C1A] pl-4 py-2 bg-[#F4F1EA] rounded-r" data-testid="admin-review-owner-reply">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#3F5F3F] font-semibold mb-1">Your reply</p>
                    <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{r.owner_reply}</p>
                    <div className="mt-2 flex gap-2">
                      <Button data-testid="admin-review-edit-reply" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => startReply(r)}>Edit reply</Button>
                      <Button data-testid="admin-review-remove-reply" size="sm" variant="ghost" className="h-7 text-xs text-red-700 hover:text-red-800" onClick={() => saveReply(r.id, true)}>Remove reply</Button>
                    </div>
                  </div>
                )}
                {replyingId === r.id && (
                  <div className="mt-4 border border-[#E5E2DC] rounded-lg p-3 bg-[#F9F9F6]">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold mb-2">Reply as Jenova</p>
                    <Textarea
                      data-testid="admin-review-reply-input"
                      rows={3}
                      value={replyDraft}
                      onChange={(e) => setReplyDraft(e.target.value)}
                      placeholder="Thank you so much for the kind words — we're thrilled you're loving it!"
                      maxLength={2000}
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={cancelReply} disabled={savingReply} className="rounded-full">Cancel</Button>
                      <Button
                        data-testid="admin-review-save-reply"
                        size="sm"
                        disabled={savingReply || !replyDraft.trim()}
                        onClick={() => saveReply(r.id)}
                        className="rounded-full bg-[#1A1C1A] hover:bg-[#3C3E3C]"
                      >
                        {savingReply ? "Saving…" : (r.owner_reply ? "Update reply" : "Post reply")}
                      </Button>
                    </div>
                  </div>
                )}
                {!r.owner_reply && replyingId !== r.id && (
                  <Button
                    data-testid="admin-review-reply"
                    size="sm"
                    variant="outline"
                    className="mt-3 rounded-full"
                    onClick={() => startReply(r)}
                  >
                    <MessageCircle className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Reply publicly
                  </Button>
                )}

                <p className="mt-3 text-[10px] text-stone-400">{new Date(r.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
